import { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import DoneIcon from "@material-ui/icons/Done";
import makeStyles from "@material-ui/core/styles/makeStyles";
import TOKEN_ERROR from "common/constants/token-error.constants";
import { removeAllSessionStorageForUndoRedo } from "features/budget/workforce/views/employee-data-tab/state/undo-redo-utils";

import { useStep } from "features/budget/wizard/state/wizard.hooks";
import steps from "features/budget/wizard/state/stepper.data";
import BudgetSteps from "features/budget/wizard/views/stepper/budget-steps.component";
import stepperStyles from "features/budget/wizard/views/stepper/stepper.styles";
import {
  getCallingNextCallback,
  getFetching,
  getRequestingNext,
  getSubmitting,
  getValid,
} from "features/budget/wizard/state/wizard.selectors";
import { BUDGET_STEPS_ROUTES } from "features/budget/budget.sections";
import { changeRequestingNext } from "features/budget/wizard/state/wizard.actions";
import useFinishScenario from "features/budget/wizard/state/finish/finish-scenario.hooks";
import LoadingBackdrop from "features/budget/wizard/views/loading-backdrop/loading-backdrop.container";
import {
  showErrorToast,
  showWarningToast,
  useToastDispatch,
} from "common/context/toast/toast-context";
import { getLocalStorageItem } from "common/utils/storage/local-storage";
import { IS_WORKFORCE_SCENARIO } from "features/budget/budget.constants";

const useStyles = makeStyles(stepperStyles);

function Stepper() {
  const toastDispatch = useToastDispatch();
  const { finishScenario, data, loading, error } = useFinishScenario();

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const classes = useStyles();

  const history = useHistory();
  const dispatchRdx = useDispatch();

  const isCallingNextCallback = useSelector(getCallingNextCallback);
  const step = useStep();

  const isValid = useSelector(getValid);
  const isSubmitting = useSelector(getSubmitting);
  const isRequestingNext = useSelector(getRequestingNext);
  const isFetching = useSelector(getFetching);

  useEffect(() => {
    async function handleFinishError() {
      const { message: errorMessage } = await error.json();

      if (errorMessage === TOKEN_ERROR) {
        const redirectToLogin = () => {
          history.push("/");
        };

        showWarningToast(
          toastDispatch,
          "Your session expired. Redirecting to sign in page...",
        );

        setTimeout(redirectToLogin, 3000);
      } else {
        showErrorToast(toastDispatch, "Error saving scenario", errorMessage);
      }
    }

    if (error !== null) {
      handleFinishError();
    }
  }, [error, history, toastDispatch]);

  const backDisabled = step === 0 || isRequestingNext || isFetching;
  const nextDisabled =
    !isValid || isSubmitting || isRequestingNext || isFetching;

  const handleBackClick = () => {
    let prevStep;
    const isWorkforceScenario = getLocalStorageItem(IS_WORKFORCE_SCENARIO);
    prevStep = BUDGET_STEPS_ROUTES[step - 1];
    if ((step === 4 || step === 7) && isWorkforceScenario === "false") {
      prevStep = BUDGET_STEPS_ROUTES[step - 2];
    }
    if (step === 5) {
      removeAllSessionStorageForUndoRedo();
    }
    history.push(prevStep);
  };

  const handleNextClick = () => {
    if (step === steps[steps.length - 1].key) {
      finishScenario();
    } else {
      dispatchRdx(changeRequestingNext(true));
    }
  };

  const [backContent, nextContent] = useMemo(() => {
    const backButtonContent =
      isLargeScreen === true ? "Back" : <ChevronLeftIcon />;
    let nextButtonContent =
      isLargeScreen === true ? "Next" : <ChevronRightIcon />;

    const isLastStep = step === steps[steps.length - 1].key;
    if (isLastStep === true) {
      nextButtonContent = isLargeScreen === true ? "Finish" : <DoneIcon />;
    }

    return [backButtonContent, nextButtonContent];
  }, [isLargeScreen, step]);

  return (
    <>
      <div className={classes.steps}>
        <BudgetSteps activeStep={step} isLargeScreen={isLargeScreen} />
        <span
          className={clsx(
            classes.buttonsContainer,
            isLargeScreen === true && classes.buttonsContainerBig,
          )}
        >
          <Button
            variant="contained"
            size="small"
            onClick={handleBackClick}
            disabled={backDisabled}
            className={clsx(
              classes.button,
              isLargeScreen === true && classes.bigButton,
            )}
          >
            {backContent}
          </Button>
          {isCallingNextCallback ? (
            <CircularProgress size={30} />
          ) : (
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={handleNextClick}
              disabled={nextDisabled}
              className={clsx(
                classes.button,
                isLargeScreen === true && classes.bigButton,
              )}
            >
              {nextContent}
            </Button>
          )}
        </span>
      </div>

      <LoadingBackdrop success={data !== null} loading={loading} />
    </>
  );
}

export default Stepper;
